import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'laravel-precognition-react-inertia';
import { PageComponent } from '@/types';
import { usePage } from '@inertiajs/react';

import MainLayout from '@/Layouts/User/MainLayout';
import Button from '@/Components/User/Button/button';
import InputText from '@/Components/User/Form/inputText';
import InputPassword from '@/Components/User/Form/inputPassword';
import TextLink from '@/Components/User/TextLink/textLink';
import SideLogin from '@/Components/User/SideMenu/SideLogin';
import FormItem from '@/Components/User/Form/formItem';
import Error from '@/Components/User/Form/Error';
import useScrollToErrorField from '@/Components/User/Hooks/scrollToErrorField';

import NaviLogo from '@/Components/SVG/NaviLogo';

const LoginIndex: PageComponent = () => {
    const form = useForm('post', route('login'), {
        mobile_tel: '',
        password: '',
    });
    const { errors } = usePage().props;
    useScrollToErrorField(errors);

    const [mobileLockStateMessage, setMobileLockStateMessage] = useState('');
    const [passwordLockStateMessage, setPasswordLockStateMessage] = useState('');

    const initialInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleKeyUp = (event: KeyboardEvent) => {
            let message = '';

            if (event.getModifierState?.('CapsLock')) {
                message += 'CapsLockがONになっています。';
            }

            if (event.getModifierState?.('ScrollLock')) {
                message += 'ScrollLockがONになっています。';
            }

            if (event.target === initialInputRef.current) {
                setMobileLockStateMessage(message);
            } else if (event.target === passwordInputRef.current) {
                setPasswordLockStateMessage(message);
            }
        };

        // 携帯番号フィールドに対して追加
        const mobileTelElement = initialInputRef.current;
        if (mobileTelElement) {
            mobileTelElement.addEventListener('keyup', handleKeyUp);
        }

        // パスワードフィールドに対してキーボードイベントを追加
        const passwordElement = passwordInputRef.current;
        if (passwordElement) {
            passwordElement.addEventListener('keyup', handleKeyUp);
        }

        // 初期化時にキーボードイベントをトリガー
        window.onload = function () {
            document.dispatchEvent(new KeyboardEvent('keyup', { key: 'a' }));
        };

        // ページロード時に携帯電話の入力フィールドにフォーカス
        if (initialInputRef.current) {
            initialInputRef.current.focus();
        }

        return () => {
            if (mobileTelElement) {
                mobileTelElement.removeEventListener('keyup', handleKeyUp);
            }
            if (passwordElement) {
                passwordElement.removeEventListener('keyup', handleKeyUp);
            }
        };
    }, []);

    const submit = (e: any) => {
        e.preventDefault();
        form.submit({
            preserveScroll: true,
            onSuccess: () => form.reset(),
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        form.setData(e.target.name, e.target.value);
    };

    return (
        <>
            <div className="login-index">
                <div className="login-index__inner">
                    <div className="login-index__head">
                        <NaviLogo />
                        <span>ログイン</span>
                    </div>

                    <div className="login-index__content">
                        <div className="login-index__main">
                            <div className="login-index__main__form">
                                <div className="login-index__main__form-title">アカウント登録がお済みの方</div>
                                <form className="form" onSubmit={submit}>
                                    <div className="form__items">
                                        {/* 携帯電話 */}
                                        <FormItem
                                            title="携帯番号"
                                            className={`${form.invalid('auth') ? 'is-error' : ''}`}
                                        >
                                            <InputText
                                                id={'mobile_tel'}
                                                type={'number'}
                                                name={'mobile_tel'}
                                                placeholder="09012345678"
                                                value={form.data.mobile_tel}
                                                onChange={handleChange}
                                                ref={initialInputRef}
                                                required={true}
                                            />

                                            {/* CapsLock/ScrollLockの状態表示 */}
                                            {mobileLockStateMessage && (
                                                <div className="form__error">
                                                    <span>{mobileLockStateMessage}</span>
                                                </div>
                                            )}
                                        </FormItem>

                                        {/* パスワード */}
                                        <FormItem title="パスワード" variationClassName="password">
                                            <InputPassword
                                                id={'password'}
                                                name={'password'}
                                                value={form.data.password}
                                                placeholder="パスワード"
                                                onChange={handleChange}
                                                ref={passwordInputRef}
                                                required={true}
                                            />
                                            <Error message={form.invalid('auth') ? form.errors.auth : null} />

                                            {/* CapsLock/ScrollLockの状態表示 */}
                                            {passwordLockStateMessage && (
                                                <div className="form__error">
                                                    <span>{passwordLockStateMessage}</span>
                                                </div>
                                            )}
                                        </FormItem>

                                        <div className="form__other">
                                            <TextLink href={route('password.request')} text="パスワードをお忘れの方" />
                                        </div>
                                    </div>

                                    <div className="form__submit">
                                        <Button
                                            text="ログイン"
                                            type="submit"
                                            className="btn-font-large__bold btn-color-primary btn-size-base"
                                            id="js-login-submit"
                                        />
                                    </div>
                                </form>
                            </div>

                            <div className="login-index__main__add">
                                <div className="login-index__main__add-title">アカウント登録がお済みでない方</div>

                                <Button
                                    href={route('register.index')}
                                    text="新規アカウント登録"
                                    className="btn btn-font-large__bold btn-color-secondary btn-size-base"
                                />
                                <div className="login-index__main__add-para">
                                    こちらは仲介業者様専用です。
                                    <br />
                                    一般のお客様はご利用できませんのでご了承ください。
                                </div>
                                <SideLogin sp />
                            </div>

                            {/* side menu */}
                            <SideLogin pc />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

LoginIndex.layout = (page) => <MainLayout title="ログイン" children={page} />;

export default LoginIndex;
